import React, { useEffect, useRef, useState } from "react";
import { Box, Button, IconButton, TextField, Typography, Alert, Skeleton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PageTitle from "../../components/PageTitle/PageTitle";
import { useSite } from "../../context/siteContext";
import OrderListItem from "../../components/OrderListItem/OrderListItem";
import OrderTracker from "../../components/OrderTracker/OrderTracker";
import OrderDetails from "../../components/OrderDetails/OrderDetails";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../../context/cartContext";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import { getRecentOrdersBySite, searchTrackOrders } from "../../services/trackOrderService";
import { getOrderTrackingDetails } from "../../services/orderHistoryService";
import { toast } from "../../context/ToastContext";
import { useAuth } from "../../context/authContext";
import { trackEvent } from "../../utilities/analyticsService";

const TrackOrder = () => {
	const { selectedSite } = useSite();
	const [recentLoading, setRecentLoading] = useState(false);
	const [recentOrders, setRecentOrders] = useState([]);
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [searchResults, setSearchResults] = useState([]);
	const [searchLoading, setSearchLoading] = useState(false);
	const [searchFeedback, setSearchFeedback] = useState(null);
	const [selectedOrder, setSelectedOrder] = useState(null);
	const [trackingLoadingOrder, setTrackingLoadingOrder] = useState(null);
	const location = useLocation();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { addMultipleToCart } = useCart();
	const { canViewProductPrice, canAddToCart } = useAuth();
	const previousSearchRef = useRef("");
	const lastTrackedSearchRef = useRef("");
	const trackingFetchedRef = useRef(new Set());
	const canShowOrderPrices = canViewProductPrice();
	const canShowBuyAgain = canAddToCart();

	useEffect(() => {
		if (!selectedSite) return;
		const run = async () => {
			setRecentLoading(true);
			try {
				const resp = await getRecentOrdersBySite({ site_number: selectedSite, limit: 3 });
				const arr = resp?.data?.data || resp?.data || [];
				setRecentOrders(arr);
			} catch (e) {
				console.error("TrackOrder: fetch error", e);
				setRecentOrders([]);
			} finally {
				setRecentLoading(false);
			}
		};
		run();
	}, [selectedSite]);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedSearch(search.trim());
		}, 400);
		return () => clearTimeout(handler);
	}, [search]);

	useEffect(() => {
		if (!selectedSite) return;
		if (!debouncedSearch) {
			setSearchResults([]);
			setSearchFeedback(null);
			setSearchLoading(false);
			return;
		}

		let isMounted = true;
		const fetchSearchResults = async () => {
			setSearchLoading(true);
			setSearchFeedback(null);
			try {
				const resp = await searchTrackOrders({
					site_number: selectedSite,
					searchTerm: debouncedSearch,
				});
				if (!isMounted) return;
				if (resp?.success) {
					const data = resp?.data || [];
					setSearchResults(data);
					const trackingKey = `${debouncedSearch.toLowerCase()}|${selectedSite}|${data.length}`;
					if (lastTrackedSearchRef.current !== trackingKey) {
						lastTrackedSearchRef.current = trackingKey;
						trackEvent("search_performed", {
							search_term: debouncedSearch,
							search_source: "track_order",
							page_name: "Track Order",
							page_path: `${location.pathname}${location.search || ""}`,
							result_count: data.length,
							has_results: data.length > 0,
							no_results: data.length === 0,
						});
					}
					const exactNumber = resp?.exactMatch;
					if (exactNumber) {
						const exactOrder = data.find(
							(entry) => String(entry?.order?.order_number || "").toLowerCase() === exactNumber.toLowerCase()
						);
						if (exactOrder) {
							handleOrderSelection(exactOrder);
						}
					}
				} else {
					setSearchResults([]);
					const trackingKey = `${debouncedSearch.toLowerCase()}|${selectedSite}|0`;
					if (lastTrackedSearchRef.current !== trackingKey) {
						lastTrackedSearchRef.current = trackingKey;
						trackEvent("search_performed", {
							search_term: debouncedSearch,
							search_source: "track_order",
							page_name: "Track Order",
							page_path: `${location.pathname}${location.search || ""}`,
							result_count: 0,
							has_results: false,
							no_results: true,
						});
					}
					setSearchFeedback({
						severity: "warning",
					message: resp?.message || t("NOTIFICATIONS.NO_ORDERS_FOR_SITE"),
					});
					handleOrderResetFromSearch();
				}
			} catch (error) {
				console.error("TrackOrder: search error", error);
				if (!isMounted) return;
				setSearchResults([]);
				setSearchFeedback({
					severity: "error",
					message: t("UI_TEXT.NO_RESULT_FOUND"),
				});
				handleOrderResetFromSearch();
			} finally {
				if (isMounted) {
					setSearchLoading(false);
				}
			}
		};

		fetchSearchResults();
		return () => {
			isMounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearch, selectedSite]);

	// When open_number param is provided, pre-fill the search box to trigger lookup
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const openNumber = params.get('open_number');
		if (openNumber) {
			setSearch(openNumber);
		}
	}, [location.search]);

	useEffect(() => {
		const trimmed = search.trim();
		if (previousSearchRef.current && trimmed === "") {
			setSearchResults([]);
			setSearchFeedback(null);
			handleOrderResetFromSearch();
		}
		previousSearchRef.current = trimmed;
	}, [search]);

	const fetchTrackingDetailsForOrder = async (orderEntry) => {
		if (!orderEntry || orderEntry?.source !== "past") return;

		const orderNumber = orderEntry?.order?.order_number || orderEntry?.order?.order_id;
		if (!orderNumber) return;
		if (trackingFetchedRef.current.has(orderNumber)) return;

		setTrackingLoadingOrder(orderNumber);
		setSelectedOrder((prev) => {
			if (!prev) return prev;
			const updatedProducts = (prev.products || []).map((product) => ({
				...product,
				tracking_details: null,
			}));
			return { ...prev, products: updatedProducts };
		});

		try {
			const response = await getOrderTrackingDetails({
				order_number: orderNumber,
				site_number: selectedSite,
			});

			if (!response?.status) {
				return;
			}

			const trackingList = Array.isArray(response.data) ? response.data : [];
			const trackingMap = new Map(
				trackingList.map((entry) => [
					String(entry?.item_number || ""),
					Array.isArray(entry?.tracking_details) ? entry.tracking_details : [],
				])
			);

			setSelectedOrder((prev) => {
				if (!prev) return prev;
				const updatedProducts = (prev.products || []).map((product) => {
					const productNumber =
						product?.productsData?.product_number ||
						product?.product_number ||
						product?.productsData?.pnum ||
						"";
					const trackingDetails = trackingMap.get(String(productNumber)) || [];
					return { ...product, tracking_details: trackingDetails };
				});
				return { ...prev, products: updatedProducts };
			});
		} catch (error) {
			console.error("TrackOrder: tracking error", error);
		} finally {
			trackingFetchedRef.current.add(orderNumber);
			setTrackingLoadingOrder(null);
		}
	};

	const handleOrderResetFromSearch = () => {
		setSelectedOrder(null);
		navigate({ pathname: location.pathname, search: "" }, { replace: true });
	};

	const handleOrderSelection = (orderEntry, syncUrl = true) => {
		if (!orderEntry) return;
		setSelectedOrder(orderEntry);
		if (syncUrl) {
			const orderNumber = orderEntry?.order?.order_number;
			if (orderNumber) {
				const params = new URLSearchParams();
				params.set("open_number", orderNumber);
				navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
			}
		}
		fetchTrackingDetailsForOrder(orderEntry);
	};

	const triggerSearch = () => {
		if (!search.trim()) return;
		setDebouncedSearch(search.trim());
	};

	const onRecentTrack = ({ order }) => {
		const payload = order?.fullOrder || recentOrders.find((o) => String(o?.order?.order_number) === String(order?.order_number));
		if (payload) {
			handleOrderSelection(payload, false);
		}
	};

	const renderOrderSkeletons = (count = 3) =>
		Array.from({ length: count }).map((_, idx) => (
			<Box
				key={`order-skel-${idx}`}
				sx={{
					width: "100%",
					maxHeight: "75px",
					borderRadius: 2,
					display: "flex",
					flexDirection: "row",
					justifyContent: "space-between",
					alignItems: "center",
					backgroundColor: "#fff",
					boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)",
					p: 2,
					columnGap: 2,
					margin: "7px 0",
				}}
			>
				<Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, width: "60%" }}>
					<Skeleton variant="text" width="80%" height={18} />
					<Skeleton variant="text" width="60%" height={14} />
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", columnGap: 1 }}>
					<Skeleton variant="rounded" width={120} height={32} />
					<Skeleton variant="rounded" width={150} height={32} />
				</Box>
			</Box>
		));

	const handleOrderAgain = async () => {
		if (!selectedOrder || !canShowBuyAgain) return;
		const products = Array.isArray(selectedOrder.products) ? selectedOrder.products : [];
		const addedProducts = [];
		const skippedProducts = [];

		const aggregated = new Map();
		for (const p of products) {
			const canBuy = p?.productsData && p?.productsData?.purchasable == 1;
			if (canBuy) {
				const pid = p?.productsData?.product_id || p?.product_id;
				const qty = p?.product_quantity || 1;
				const prev = aggregated.get(pid) || 0;
				aggregated.set(pid, prev + qty);
				addedProducts.push(p?.productsData?.product_name);
			} else {
				skippedProducts.push(p?.productsData?.product_name || `Product ID ${p?.product_id}`);
			}
		}

		const items = Array.from(aggregated.entries()).map(([ProductID, Quantity]) => ({ ProductID, Quantity, Site: selectedSite }));
		if (items.length > 0) {
			await addMultipleToCart(items);
		}

		if (addedProducts.length > 0 && skippedProducts.length === 0) {
			toast.trigger({
				severity: "success",
				message: `${t('GENERAL.ALL_PARTS_FROM_ORDER')} ${selectedOrder?.order?.order_number} ${t('GENERAL.WERE_ADDED_TO_YOUR_CART')}.`,
			});
		} else if (addedProducts.length > 0 && skippedProducts.length > 0) {
			toast.trigger({
				severity: "warning",
				message: `${t('GENERAL.SOME_PARTS_WERE_ADDED_UNAVAILABLE')} ${skippedProducts.join(", ")}.`,
			});
		} else if (addedProducts.length === 0 && skippedProducts.length > 0) {
			toast.trigger({
				severity: "error",
				message: t('GENERAL.UNABLE_TO_ADD_PARTS_ALL_PRODUCTS_FROM_THIS_ORDER_ARE_UNAVAILABLE'),
			});
		}
	};

	return (
		<Box sx={{ p: 2, width: '80vw', mx: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', height: '100%', minHeight: '650px' }}>
			<PageTitle title={t('LABELS.TRACK_ORDER')} />

			{/* Search Bar - same UI style as GlobalSearch */}
			<Box sx={{ mb: 2 }}>
				<TextField
					variant="outlined"
					placeholder= {t('GENERAL.SEARCH_BY_ORDER_PO')}
					fullWidth
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					onKeyDown={(e) => { if (e.key === 'Enter') triggerSearch(); }}
					size="small"
					slotProps={{
						input: {
							endAdornment: (
								<Box display="flex">
									<IconButton marginright="-2px" onClick={triggerSearch}>
										<SearchIcon />
									</IconButton>
								</Box>
							),
						},
					}}
					sx={{
						"& .MuiInputBase-root": { paddingRight: "2px" },
						"& .MuiOutlinedInput-root": { borderRadius: "40px", backgroundColor: "#fff" },
						width: "100%",
					}}
				/>
			</Box>



			{/* Recent Orders */}
			{search.trim().length === 0 && (
				<Box sx={{ mt: 2, px: 1 }}>
					<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>{t('LABELS.RECENT')}</Typography>
					{recentLoading && renderOrderSkeletons(3)}
					{!recentLoading && recentOrders.length === 0 && (
						<Alert severity="info">{t('NOTIFICATIONS.NO_ORDERS_FOR_SITE')}</Alert>
					)}
					{recentOrders.map((o, idx) => (
						<OrderListItem
							key={`recent-${o?.order?.order_id || idx}`}
							order={{
								name: o?.order?.order_number,
								order_id: o?.order?.order_id,
								order_number: o?.order?.order_number,
								order_po: o?.details?.po_number,
								details: o?.details,
								fullOrder: o,
							}}
							buttonText={t('LABELS.TRACK_ORDER')}
							onTrack={onRecentTrack}
						/>
					))}
				</Box>
			)}

			{search.trim().length > 0 && (
				<Box sx={{ mt: 2, px: 1 }}>
					<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>{t('UI_TEXT.SEARCH_RESULTS')}</Typography>
					{searchLoading && renderOrderSkeletons(3)}
					{!searchLoading && searchResults.map((o, idx) => (
						<OrderListItem
							key={`search-${o?.order?.order_id || o?.order?.order_number || idx}`}
							order={{
								name: o?.order?.order_number || o?.order?.order_key,
								order_id: o?.order?.order_id || o?.order?.order_key,
								order_number: o?.order?.order_number,
								order_po: o?.details?.po_number,
								details: o?.details,
								fullOrder: o,
							}}
							onTrack={({ order }) => handleOrderSelection(order?.fullOrder || o)}
						/>
					))}
					{!searchLoading && searchResults.length === 0 && searchFeedback && (
						<Alert severity={searchFeedback.severity}>{searchFeedback.message}</Alert>
					)}
				</Box>
			)}

			{/* Selected Order Details */}
			{selectedOrder && (
				<Box sx={{ mt: 3, px: 1 }}>
					<PageTitle title={`${t('TITLES.ORDER_DETAILS_1')}: ${selectedOrder?.order?.order_number}`} />
					<Box className="track-order-container" sx={{ display: 'flex', columnGap: 1, mb: 1 }}>
						<Button variant="outlined" color="primary" onClick={handleOrderResetFromSearch}>
							{t('BUTTON.CANCEL')}
						</Button>
						{canShowBuyAgain && (
							<Button variant="contained" startIcon={<AddShoppingCartIcon />} color="primary" onClick={handleOrderAgain}>
								{t('BUTTON.BUY_AGAIN')}
							</Button>
						)}
					</Box>
					<div className="order-tracker-container"></div>
					<OrderTracker orderData={selectedOrder} />
					<OrderDetails
						orderData={selectedOrder}
						trackingLoading={
							trackingLoadingOrder &&
							String(trackingLoadingOrder) === String(selectedOrder?.order?.order_number || selectedOrder?.order?.order_id)
						}
						showPrices={canShowOrderPrices}
					/>
					<Box className="all-orders-container" sx={{ display: 'flex', columnGap: 1, mt: 1 }}>
						{canShowBuyAgain && (
							<Button variant="contained" startIcon={<AddShoppingCartIcon />} color="primary" onClick={handleOrderAgain}>
								{t('BUTTON.BUY_AGAIN')}
							</Button>
						)}
						<Button variant="outlined" color="primary" onClick={handleOrderResetFromSearch}>
							{t('BUTTON.CANCEL')}
						</Button>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default TrackOrder;
